import { useCallback, useState } from "react";
import { Mail, X, Copy } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { User } from "../AuthContext";

type InviteUserModalProps = {
    showInviteModal: boolean;
    roomId: string;
    user: User | null;
    setShowInviteModal: (show: boolean) => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ showInviteModal, roomId, user, setShowInviteModal }) => {
    const functions = getFunctions();
    const [inviteEmail, setInviteEmail] = useState<string>("");
    const [isInviting, setIsInviting] = useState<boolean>(false);
    const [inviteMessage, setInviteMessage] = useState<string>("");
    const [inviteMessageType, setInviteMessageType] = useState<"success" | "error" | null>(null);
    const [roomLink, setRoomLink] = useState<string>("");

    const inviteUserToRoomCallable = httpsCallable<
        { roomId: string; email: string },
        { success: boolean; message: string; userId: string }
    >(functions, "inviteUserToRoom");

    // Função para convidar usuário por e-mail usando Cloud Function
    const handleInviteUser = useCallback(async () => {
        if (!inviteEmail.trim() || !roomId || !user || isInviting) return;

        setIsInviting(true);
        setInviteMessage("");
        setInviteMessageType(null);

        try {
            const result = await inviteUserToRoomCallable({
                roomId: roomId,
                email: inviteEmail.trim(),
            });

            if (result.data.success) {
                // Gera link da sala
                const link = `${window.location.origin}/sala/${roomId}`;
                setRoomLink(link);

                setInviteMessage("Convite enviado com sucesso! Link da sala gerado:");
                setInviteMessageType("success");
                setInviteEmail("");
            } else {
                setInviteMessage(result.data.message || "Erro ao enviar convite");
                setInviteMessageType("error");
            }
        } catch (error: any) {
            console.error("❌ Erro ao convidar usuário:", error);

            // Trata erros específicos das Cloud Functions
            let errorMessage = "Erro ao enviar convite";
            if (error.code) {
                switch (error.code) {
                    case "functions/unauthenticated":
                        errorMessage = "Você precisa estar logado para convidar usuários";
                        break;
                    case "functions/permission-denied":
                        errorMessage =
                            "Você não tem permissão para convidar usuários nesta sala";
                        break;
                    case "functions/not-found":
                        errorMessage =
                            error.message || "Usuário não encontrado com este e-mail";
                        break;
                    case "functions/invalid-argument":
                        errorMessage = "Dados inválidos fornecidos";
                        break;
                    default:
                        errorMessage = error.message || "Erro ao enviar convite";
                }
            }

            setInviteMessage(errorMessage);
            setInviteMessageType("error");
        } finally {
            setIsInviting(false);
        }
    }, [inviteEmail, roomId, user]);

    // Função para copiar link da sala
    const handleCopyLink = useCallback(async () => {
        if (!roomLink) return;

        try {
            await navigator.clipboard.writeText(roomLink);
            setInviteMessage("Link copiado para a área de transferência!");
            setInviteMessageType("success");
        } catch (error) {
            setInviteMessage("Não foi possível copiar o link");
            setInviteMessageType("error");
        }
    }, [roomLink]);

    if (!showInviteModal) return null;

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[110] flex justify-center items-center p-4"
            onClick={() => {
                setShowInviteModal(false);
                setInviteEmail("");
                setInviteMessage("");
                setInviteMessageType(null);
                setRoomLink("");
            }}
        >
            <div
                className="bg-white rounded-lg w-full max-w-sm max-h-[80vh] overflow-y-auto p-6 space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Convidar Usuário
                    </h3>
                    <button
                        onClick={() => {
                            setShowInviteModal(false);
                            setInviteEmail("");
                            setInviteMessage("");
                            setInviteMessageType(null);
                            setRoomLink("");
                        }}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            E-mail do usuário
                        </label>
                        <input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="exemplo@email.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            disabled={isInviting}
                        />
                    </div>

                    {inviteMessage && (
                        <div
                            className={`p-3 rounded-lg text-sm ${inviteMessageType === "success"
                                ? "bg-green-50 text-green-800 border border-green-200"
                                : "bg-red-50 text-red-800 border border-red-200"
                                }`}
                        >
                            {inviteMessage}

                            {/* Link da sala quando sucesso */}
                            {inviteMessageType === "success" && roomLink && (
                                <div className="mt-2 space-y-2">
                                    <div className="bg-gray-50 p-2 rounded border break-all text-xs">
                                        {roomLink}
                                    </div>
                                    <button
                                        onClick={handleCopyLink}
                                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                                    >
                                        <Copy className="w-3 h-3" />
                                        Copiar Link
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        onClick={() => {
                            setShowInviteModal(false);
                            setInviteEmail("");
                            setInviteMessage("");
                            setInviteMessageType(null);
                            setRoomLink("");
                        }}
                        className="flex-1 px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleInviteUser}
                        disabled={!inviteEmail.trim() || isInviting}
                        className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                        {isInviting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Mail className="w-4 h-4" />
                                Enviar Convite
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default InviteUserModal;